import React, { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';

const InventoryMIS = () => {
  const [grouped, setGrouped] = useState({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(delay);
  }, [search]);

  const groupByMonth = useCallback((records) => {
    const filtered = records.filter(record =>
      record.warehouse.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      record.material.toLowerCase().includes(debouncedSearch.toLowerCase())
    );

    const groupedData = {};
    filtered.forEach(record => {
      if (!groupedData[record.month]) groupedData[record.month] = [];
      groupedData[record.month].push(record);
    });
    setGrouped(groupedData);
  }, [debouncedSearch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/report/inventory-mis-get?search=${debouncedSearch.trim()}`, {
        method: 'GET',
        credentials: 'include',
      });
      const json = await res.json();
      if (json.success) {
        groupByMonth(json.data);
      } else {
        setError('Failed to load inventory data');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, groupByMonth]);

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, fetchData]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Inventory MIS</h2>
          <p className="text-sm text-gray-500 mt-1">Monthly warehouse-wise material stock summary</p>
        </div>
        <div className="relative w-full sm:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeWidth={2} />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by warehouse or material..."
            value={search}
            onChange={handleSearchChange}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading Inventory MIS data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <NoRecordFound message="No records found" />
      ) : (
        Object.entries(grouped).map(([month, records]) => (
          <div key={month} className="mb-6">
            <h3 className="text-base font-semibold text-blue-600 mb-2">{month}</h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-700 font-semibold">
                  <tr>
                    <th className="px-4 py-2 text-left">Warehouse</th>
                    <th className="px-4 py-2 text-left">Material</th>
                     <th className="px-4 py-2 text-left">Vessel Name</th>
                    <th className="px-4 py-2 text-left">Origin Country</th>
                    <th className="px-4 py-2 text-right">Opening Stock (MT)</th>
                    <th className="px-4 py-2 text-right">Inward Qty (MT)</th>
                    <th className="px-4 py-2 text-right">Outward Qty (MT)</th>
                    <th className="px-4 py-2 text-right">Current Stock (MT)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {records.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{row.warehouse}</td>
                      <td className="px-4 py-3">{row.material}</td>
                      <td className="px-4 py-3">{row.vessel_name}</td>
                      <td className="px-4 py-3">{row.origin_country}</td>
                      <td className="px-4 py-3 text-right">{Number(row.opening_stock_mt).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{Number(row.inward_qty_mt).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{Number(row.outward_qty_mt).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{Number(row.current_stock_mt).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default InventoryMIS;
